import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export default function PayoutManagement({ user, onLogout }) {
  const [payouts, setPayouts] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    investment_id: '',
    investor_id: '',
    amount: '',
    payout_type: 'interest'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [payoutsRes, investmentsRes] = await Promise.all([
        axios.get(`${API}/admin/payouts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/investments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPayouts(payoutsRes.data);
      setInvestments(investmentsRes.data.filter(inv => inv.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvestmentSelect = (investmentId) => {
    const investment = investments.find(i => i.investment_id === investmentId);
    if (investment) {
      setFormData({
        ...formData,
        investment_id: investmentId,
        investor_id: investment.investor_id
      });
    }
  };

  const handleAddPayout = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      await axios.post(`${API}/admin/payouts`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payout created successfully');
      setShowAddDialog(false);
      setFormData({
        investment_id: '',
        investor_id: '',
        amount: '',
        payout_type: 'interest'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payout');
    }
  };

  const filteredPayouts = payouts.filter(p =>
    p.payout_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.investor_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.investment_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout user={user} onLogout={onLogout} currentPage="payouts">
      <div className="space-y-6" data-testid="payout-management-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Payouts
            </h1>
            <p className="text-muted-foreground text-lg">Process investor payouts</p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <button data-testid="add-payout-button" className="btn-primary flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Process Payout</span>
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Process New Payout</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddPayout} className="space-y-4" data-testid="add-payout-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Investment *</label>
                  <select
                    data-testid="payout-investment-select"
                    required
                    value={formData.investment_id}
                    onChange={(e) => handleInvestmentSelect(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select Investment</option>
                    {investments.map(inv => (
                      <option key={inv.investment_id} value={inv.investment_id}>
                        {inv.investment_id} - ₹{inv.principal_amount.toLocaleString('en-IN')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Payout Type *</label>
                  <select
                    data-testid="payout-type-select"
                    required
                    value={formData.payout_type}
                    onChange={(e) => setFormData({...formData, payout_type: e.target.value})}
                    className="input-field"
                  >
                    <option value="interest">Interest Payout</option>
                    <option value="principal">Principal Payout</option>
                    <option value="maturity">Maturity Payout</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                  <input
                    data-testid="payout-amount-input"
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="input-field"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddDialog(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button data-testid="submit-payout-button" type="submit" className="btn-primary">
                    Process Payout
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="card">
          <div className="p-6 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="search-payouts-input"
                type="text"
                placeholder="Search payouts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payouts-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Payout ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Investor ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Gross Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    TDS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Net Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredPayouts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                      No payouts found
                    </td>
                  </tr>
                ) : (
                  filteredPayouts.map((payout) => (
                    <tr key={payout.payout_id} data-testid={`payout-row-${payout.payout_id}`} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm">{payout.payout_id}</td>
                      <td className="px-6 py-4 font-mono text-sm">{payout.investor_id}</td>
                      <td className="px-6 py-4 font-mono">
                        ₹{(payout.gross_amount || payout.amount).toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4">
                        {payout.tds_amount > 0 ? (
                          <span className="text-amber-600 font-medium">
                            -₹{payout.tds_amount.toLocaleString('en-IN')}
                            <span className="text-xs text-muted-foreground ml-1">({payout.tds_rate}%)</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">No TDS</span>
                        )}
                      </td>
                      <td className="px-6 py-4 font-mono font-semibold text-success">
                        ₹{payout.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 capitalize">{payout.payout_type}</td>
                      <td className="px-6 py-4 text-sm">
                        {new Date(payout.payout_date).toLocaleDateString('en-IN')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}